"use client";

import { useEffect, useState } from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Shield, Smartphone, Sparkles, Star, Terminal, TrendingUp, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metrics = [{
  id: "health",
  label: "Website Health",
  valueNum: 99,
  valueSuffix: "/100",
  isText: false,
  textVal: "",
  progressVal: 99,
  statusText: "Optimal",
  iconName: "activity"
}, {
  id: "speed",
  label: "Page Speed",
  valueNum: 0.4,
  valueSuffix: "s LCP",
  isText: false,
  textVal: "",
  progressVal: 96,
  statusText: "Ultra Fast",
  iconName: "zap"
}, {
  id: "mobile",
  label: "Mobile Optimization",
  valueNum: 99.8,
  valueSuffix: "%",
  isText: false,
  textVal: "",
  progressVal: 99.8,
  statusText: "Responsive",
  iconName: "mobile"
}, {
  id: "conversion",
  label: "Conversion Rate",
  valueNum: 4.8,
  valueSuffix: "x",
  isText: false,
  textVal: "",
  progressVal: 88,
  statusText: "+320% Avg",
  iconName: "trending"
}, {
  id: "vitals",
  label: "Core Web Vitals",
  valueNum: 0,
  valueSuffix: "",
  isText: true,
  textVal: "All Passed",
  progressVal: 100,
  statusText: "Good Status",
  iconName: "shield"
}, {
  id: "ai_ready",
  label: "AI Search Ready",
  valueNum: 0,
  valueSuffix: "",
  isText: true,
  textVal: "Grade A+",
  progressVal: 98,
  statusText: "GEO Optimized",
  iconName: "star"
}];
export default function Performance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const [animProgress, setAnimProgress] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let frameId: number;
    const startTime = performance.now();
    const duration = 1200;
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const factor = Math.min(elapsed / duration, 1);
      const easeFactor = 1 - Math.pow(1 - factor, 3);
      setAnimProgress(easeFactor);
      if (factor < 1) {
        frameId = requestAnimationFrame(step);
      }
    };
    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [isVisible]);
  return <section data-section-id="31001" id="performancehero" ref={ref} className="relative min-h-screen w-full bg-background pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden text-foreground">
      <div className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: `
            radial-gradient(circle at 50% 30%, var(--accent-deep-red) 0%, transparent 65%),
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
      backgroundSize: "100% 100%, 40px 40px, 40px 40px"
    }} />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`mx-auto max-w-4xl text-center transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="mb-6 inline-flex items-center justify-center">
            <Badge variant="outline" className="px-4 py-1.5 border border-border bg-[hsl(240_10%_4%)] text-[var(--accent-deep-red)] font-mono text-xs tracking-[0.25em] uppercase rounded-lg shadow-sm">
              <Sparkles className="mr-2 size-3.5 inline text-[var(--accent-deep-red)]" />
              WEBSITE DESIGN & OPTIMIZATION
            </Badge>
          </div>

          <h1 className="font-default font-bold tracking-tight text-foreground text-balance" style={{
          fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
          lineHeight: 1.1
        }}>
            Websites built to earn{" "}
            <span className="italic text-[var(--accent-deep-red)]">trust</span>,
            convert visitors, and rank in AI search
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed text-balance">
            Your website must do far more than just look sleek. ClearLine
            engineers lightning-fast digital assets designed for direct client
            conversions and algorithmic visibility in modern search engines.
          </p>

          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white font-medium px-8 py-6 rounded-lg shadow-md transition-all duration-200">
              <Link to="/ContactPage">
                <span>Book a strategy call</span>
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-[hsl(240_10%_4%)] hover:bg-[hsl(240_10%_7%)] text-foreground font-medium px-8 py-6 rounded-lg shadow-xs transition-all duration-200">
              <Link to="/VisibilityCheckupPage">
                <span>Free AI visibility checkup</span>
              </Link>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-muted-foreground font-sans">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
              Sub-second loading standard
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
              GEO & AI engine optimization
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
              High conversion architecture
            </span>
          </div>
        </div>

        <div className={`mt-16 sm:mt-20 transition-all duration-1000 delay-200 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="border border-border bg-[hsl(240_10%_4%)] shadow-2xl rounded-2xl overflow-hidden p-0">
            <CardHeader className="border-b border-border bg-[hsl(240_10%_3%)] px-6 py-4 flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="size-3 rounded-lg bg-red-500/80 inline-block" />
                  <span className="size-3 rounded-lg bg-amber-500/80 inline-block" />
                  <span className="size-3 rounded-lg bg-emerald-500/80 inline-block" />
                </div>
                <div className="h-4 w-px bg-border mx-2" />
                <div className="flex items-center gap-2 font-mono text-xs sm:text-sm text-muted-foreground">
                  <Terminal className="size-4 text-[var(--accent-deep-red)]" />
                  <span>clearline.dashboard // live audit</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="relative flex size-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-lg size-2 bg-emerald-500" />
                </span>
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider hidden sm:inline-block">
                  Live System Monitor
                </span>
              </div>
            </CardHeader>

            <CardContent className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {metrics.map((item, i) => {
                let renderedValue = "";
                if (item.isText) {
                  renderedValue = item.textVal;
                } else if (item.valueNum % 1 !== 0) {
                  renderedValue = (item.valueNum * animProgress).toFixed(1);
                } else {
                  renderedValue = Math.round(item.valueNum * animProgress).toString();
                }
                const currentProgress = Math.round(item.progressVal * animProgress);
                return <div key={item.id} data-index={i} className="group relative border border-border bg-[hsl(240_10%_7%)] rounded-2xl p-5 sm:p-6 transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:border-[var(--accent-deep-red)] hover:ring-1 hover:ring-[var(--accent-deep-red)]/50 shadow-md flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="p-2.5 rounded-lg bg-background/60 border border-border/50 text-[var(--accent-deep-red)] group-hover:text-foreground transition-colors">
                            {item.iconName === "activity" && <Activity className="size-5" />}
                            {item.iconName === "zap" && <Zap className="size-5" />}
                            {item.iconName === "mobile" && <Smartphone className="size-5" />}
                            {item.iconName === "trending" && <TrendingUp className="size-5" />}
                            {item.iconName === "shield" && <Shield className="size-5" />}
                            {item.iconName === "star" && <Star className="size-5" />}
                          </div>
                          <Badge variant="secondary" className="font-mono text-[10px] bg-background/80 border border-border text-muted-foreground px-2 py-0.5 rounded-md">
                            {item.statusText}
                          </Badge>
                        </div>

                        <div className="font-sans text-xs sm:text-sm font-medium text-muted-foreground mb-1">
                          {item.label}
                        </div>

                        <div className="font-default text-2xl sm:text-3xl font-bold tracking-tight text-foreground flex items-baseline gap-1">
                          <span>{renderedValue}</span>
                          {item.valueSuffix && <span className="text-sm font-normal text-muted-foreground">
                              {item.valueSuffix}
                            </span>}
                        </div>
                      </div>

                      <div className="mt-5">
                        <div className="flex justify-between items-center text-[10px] font-mono text-muted-foreground mb-1.5">
                          <span>Performance score</span>
                          <span>{currentProgress}%</span>
                        </div>
                        <Progress value={currentProgress} className="h-1.5 bg-background rounded-md overflow-hidden" />
                      </div>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}